/** A running summary of what a visitor has done, so a lead arrives with context
 * ("2 days browsing, 3 listings, searched Mumbai") and so visitors who never
 * convert are still measurable.
 *
 * Deliberately a **summary**, not a full analytics log: GA4 already records every
 * page view and click, and its User Explorer report is the place to read a
 * journey step by step. What GA4 cannot do is travel with the lead into Pardot
 * and Salesforce — that is this file's job, and it is why the payload is six
 * small counters rather than a trail of events.
 *
 * Consent: no gate of its own. The journey is only recorded when a visitor ID
 * exists, and that ID is minted only under functional consent — so refusing
 * functional cookies stops this too, with one rule instead of two.
 */
import { getVisitorId } from "./adClickIds";

const STORAGE_KEY = "oh_journey";
/** Matches the analytics convention: 30 minutes idle ends a session. */
const SESSION_GAP_MS = 30 * 60 * 1000;
/** Kept only so the journey can be inspected in the console and in tests; never
 * sent anywhere, because a trail does not fit in a CRM field. */
const TRAIL_LIMIT = 20;

type StepKind = "page" | "listing" | "search";
type Step = { t: number; k: StepKind; v: string };

type Journey = {
  firstSeen: string;
  lastSeen: number;
  sessions: number;
  pages: number;
  listings: number;
  searches: number;
  lastSearch: string;
  trail: Step[];
};

/** `newSession` lets the caller fire the `user_journey` event once per session
 * instead of once per page — one event, not one per click. */
export type JourneyUpdate = { summary: JourneySummary; newSession: boolean };

export type JourneySummary = {
  journey_pages: number;
  journey_listings: number;
  journey_searches: number;
  journey_sessions: number;
  journey_first_seen: string;
  journey_last_search: string;
};

const blank = (): Journey => ({
  firstSeen: new Date().toISOString().slice(0, 10),
  lastSeen: 0,
  sessions: 0,
  pages: 0,
  listings: 0,
  searches: 0,
  lastSearch: "",
  trail: [],
});

function read(): Journey {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...blank(), ...(JSON.parse(stored) as Journey) } : blank();
  } catch {
    return blank();
  }
}

function write(journey: Journey): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(journey));
  } catch {
    // Private mode or a full quota: losing the journey must never break a page.
  }
}

/** A listing and a property both mean "looked at a specific space", so they share
 * one counter; the country page and `/search/...` both mean "looking for one". */
function classify(pathname: string): Step {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1] ?? "";
  const t = Date.now();

  if (segments.includes("listings") || segments.includes("properties")) {
    return { t, k: "listing", v: last };
  }
  if (segments.includes("search") || segments.some((s) => s.startsWith("office-space"))) {
    return { t, k: "search", v: last };
  }
  return { t, k: "page", v: pathname };
}

/**
 * Record one page. Called once per route change from the root layout, which is
 * why no listing, search or property component needs to know this file exists.
 */
export function recordJourneyPage(pathname: string): JourneyUpdate | null {
  if (typeof window === "undefined") return null;
  if (!getVisitorId()) return null; // no functional consent

  const journey = read();
  const step = classify(pathname);

  const newSession = step.t - journey.lastSeen > SESSION_GAP_MS;
  if (newSession) journey.sessions += 1;
  journey.lastSeen = step.t;
  journey.pages += 1;
  if (step.k === "listing") journey.listings += 1;
  if (step.k === "search") {
    journey.searches += 1;
    if (step.v) journey.lastSearch = step.v;
  }
  journey.trail = [...journey.trail, step].slice(-TRAIL_LIMIT);

  write(journey);
  return { summary: toSummary(journey), newSession };
}

function toSummary(journey: Journey): JourneySummary {
  return {
    journey_pages: journey.pages,
    journey_listings: journey.listings,
    journey_searches: journey.searches,
    journey_sessions: journey.sessions,
    journey_first_seen: journey.firstSeen,
    journey_last_search: journey.lastSearch,
  };
}

/** The six counters, for `lead_submitted` and the dataLayer. Empty object when
 * there is no journey, so callers can spread it unconditionally. */
export function getJourneySummary(): Partial<JourneySummary> {
  if (typeof window === "undefined") return {};
  if (!getVisitorId()) return {};
  const journey = read();
  return journey.pages ? toSummary(journey) : {};
}

/** One string for the CRM: a single Pardot field instead of six.
 * `pages=11;listings=3;searches=2;sessions=2;first_seen=2026-09-20;last_search=mumbai` */
export function getJourneySummaryString(): string {
  const summary = getJourneySummary();
  if (!summary.journey_pages) return "";
  return [
    `pages=${summary.journey_pages}`,
    `listings=${summary.journey_listings}`,
    `searches=${summary.journey_searches}`,
    `sessions=${summary.journey_sessions}`,
    `first_seen=${summary.journey_first_seen}`,
    `last_search=${summary.journey_last_search}`,
  ].join(";");
}

/** Called when functional cookies are withdrawn, alongside the visitor ID. */
export function clearJourney(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the next read falls back to a blank journey anyway.
  }
}

/** For debugging from the console: `__ohJourney()`. */
export function readJourneyForDebug(): Journey | null {
  if (typeof window === "undefined") return null;
  return read();
}
